import { ContentCardTypeEnum } from '@/modules/content/config/cards';
import { PageComponentTypeEnum } from '@/types/schema';

export enum WidgetDimensions {
  XXS = 'xxs',
  XS = 'xs',
  S = 's',
  M = 'm',
  L = 'l',
  XL = 'xl',
}

export type LayoutType = {
  height?: WidgetDimensions;
  width?: WidgetDimensions;
};

const getColSpan = (width?: WidgetDimensions) => {
  switch (width) {
    case WidgetDimensions.XXS:
      return 4;
    case WidgetDimensions.XS:
      return 5;
    case WidgetDimensions.S:
      return 6;
    case WidgetDimensions.M:
      return 7;
    case WidgetDimensions.L:
      return 8;
    case WidgetDimensions.XL:
      return 12;
    default:
      return 12;
  }
};

const getRowSpan = (height?: WidgetDimensions) => {
  switch (height) {
    case WidgetDimensions.L:
      return 2;
    default:
      return 1;
  }
};

export const getWidgetHeight = (height?: WidgetDimensions) => {
  switch (height) {
    case WidgetDimensions.XXS:
      return '10.8rem';
    case WidgetDimensions.XS:
      return '17.4rem';
    case WidgetDimensions.S:
      return '25.3rem';
    case WidgetDimensions.M:
      return '36.6rem';
    case WidgetDimensions.L:
      return '52.6rem';
    default:
      return '25.3rem';
  }
};

export const getLayoutSpan = (layout: LayoutType) => {
  const colSpan = getColSpan(layout?.width);
  const rowSpan = getRowSpan(layout?.height);
  const widgetHeight = getWidgetHeight(layout?.height);
  return {
    colSpan,
    rowSpan,
    widgetHeight,
  };
};

export const DEFAULT_BANNER_LAYOUT: LayoutType = {
  height: WidgetDimensions.S,
  width: WidgetDimensions.XL,
};

export const DEFAULT_LAYOUTS = {
  // todo- update as per pageComponents
  [PageComponentTypeEnum.MultiBanner]: DEFAULT_BANNER_LAYOUT,
  [PageComponentTypeEnum.Stack]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.PerformanceMetrics]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.OkrListing]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.TaskListing]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Meetings]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.MeetingInstances]: {
    height: WidgetDimensions.S,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.List]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.S,
  },
  [PageComponentTypeEnum.Gallery]: {
    height: WidgetDimensions.S,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Overview]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Calendar]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.CardGrid]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
    [ContentCardTypeEnum.Mini]: {
      height: WidgetDimensions.XS,
      width: WidgetDimensions.XL,
    },
    [ContentCardTypeEnum.Display]: {
      height: WidgetDimensions.S,
      width: WidgetDimensions.XL,
    },
    [ContentCardTypeEnum.Default]: {
      height: WidgetDimensions.M,
      width: WidgetDimensions.XL,
    },
    [ContentCardTypeEnum.Wide]: {
      height: WidgetDimensions.M,
      width: WidgetDimensions.XL,
    },
  },
  [PageComponentTypeEnum.Habits]: {
    height: WidgetDimensions.S,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.ReviewCycles]: {
    height: WidgetDimensions.S,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.SubmissionsToReview]: {
    height: WidgetDimensions.S,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.AiRecommendation]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.MgsaMetrics]: {
    height: WidgetDimensions.XXS,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Category]: {
    height: WidgetDimensions.XS,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Projects]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Teams]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Praise]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.HomepageReporting]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Flashcard]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
  [PageComponentTypeEnum.Gamification]: {
    height: WidgetDimensions.M,
    width: WidgetDimensions.XL,
  },
};

export const DEFAULT_LAYOUT_GENERIC = {
  // full-width, small height -> matches old designs
  height: WidgetDimensions.S,
  width: WidgetDimensions.XL,
};
